import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { LlmIntegrationService } from 'src/app/core/services/llm-integration.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { FormalizeCompany, FormalizePayload, FormalizeUser } from 'src/app/core/models/formalizePayload';

@Component({
    selector: 'app-bulk-formalize',
    templateUrl: './bulk-formalize.component.html',
    styleUrls: ['./bulk-formalize.component.css']
})
export class BulkFormalizeComponent implements OnInit, OnChanges {
    @Input() items: any = [];
    @Output() saveCallback = new EventEmitter<any>();

    @Input() noteId: string = null;
    @Input() sourceScreen: string = '';
    @Input() candidate: any = null;
    @Input() requisition: any = null;
    @Input() jobMatch: any = null;
    @Input() company: any = null;

    tone = 'Formal';
    isGenerating = false;
    generateError = '';
    generateSuccess = '';
    isExpanded = false;
    private requestStartTime: Date = null;

    constructor(private llm: LlmIntegrationService, private authService: AuthService) { }

    ngOnInit(): void {
        this._ensureItemsArray();
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.items) {
            this._ensureItemsArray();
        }
    }

    hasGeneratedVariants(): boolean {
        return Array.isArray(this.items) && this.items.some(item => !!(item as any).generated);
    }

    private _ensureItemsArray() {
        // When used from AngularJS (downgraded), `items` may arrive as a JSON string.
        if (typeof this.items === 'string') {
            try {
                this.items = JSON.parse(this.items);
            } catch (e) {
                this.items = [];
            }
        }
        if (!Array.isArray(this.items)) this.items = [];
        this.items.forEach(item => {
            (item as any).variants = (item as any).variants || [];
            (item as any).selectedVariantIndex = (item as any).selectedVariantIndex || -1;
            (item as any).generated = (item as any).generated || false;
        });
    }

    formalizeAll() {
        if (!this.items || this.items.length === 0) return;
        this.isGenerating = true;
        this.generateError = '';
        this.generateSuccess = '';
        this.requestStartTime = new Date();

        const promptBase = `You are an assistant that rewrites multiple pieces of text in a ${this.tone} tone. For each input return a single best "Recommended" variation. Return only valid JSON with one key "items" whose value is an array. Each element must be an object with keys: "key" and "recommended". "recommended" must be an object with fields "label" and "text" (label should be "Recommended"). Return only the JSON and nothing else.`;
        const inputs = this.items.map(item => ({ key: item.key, text: (item.text || '') }));
        const combinedPrompt = `${promptBase}\n\nInputs:\n${JSON.stringify(inputs)}`;

        const options = { n: 1 };

        this.llm.sendRequest(combinedPrompt, undefined, options, (res: any) => {
            let raw = '';
            if (res && res.choices && res.choices[0] && res.choices[0].message && res.choices[0].message.content) {
                raw = res.choices[0].message.content;
            } else if (res && res.choices && res.choices[0] && res.choices[0].text) {
                raw = res.choices[0].text;
            }

            let parsed = null;
            try {
                parsed = JSON.parse(raw.trim());
            } catch (e) {
                const start = raw.indexOf('{');
                const end = raw.lastIndexOf('}');
                if (start !== -1 && end !== -1 && end > start) {
                    try { parsed = JSON.parse(raw.substring(start, end + 1)); } catch (e2) { parsed = null; }
                }
            }

            if (parsed && parsed.items && Array.isArray(parsed.items)) {
                parsed.items.forEach(parse => {
                    const found = this.items.find(item => item.key === parse.key);
                    if (found) {
                        // If assistant returned a single recommended object
                        if (parse.recommended && (parse.recommended.text || parse.recommended.output)) {
                            const recText = (parse.recommended.text || parse.recommended.output || '').trim();
                            (found as any).variants = [{ label: parse.recommended.label || 'Recommended', text: recText }];
                            (found as any).selectedVariantIndex = 0;
                            (found as any).generated = true;
                        } else if (parse.variants && Array.isArray(parse.variants)) {
                            // Backwards compatibility: accept full variants array
                            (found as any).variants = parse.variants.map(variant => ({ label: variant.label, text: (variant.text || '').trim() }));
                            (found as any).selectedVariantIndex = 0;
                            (found as any).generated = true;
                        }
                    }
                });
                // After applying recommended variants for all items, automatically save changes
                try {
                    this.save();
                    this.generateSuccess = 'Comments enhanced successfully.';
                    setTimeout(() => { this.generateSuccess = ''; }, 4000);
                } catch (e) { /* ignore save errors here; parent handles */ }
                this.saveFormalizationActivity();
            } else {
                this.generateError = 'Unable to parse assistant response. Try again.';
            }

            this.isGenerating = false;
        }, (err) => {
            console.error(err);
            this.generateError = 'Error enhancing comments.';
            this.generateSuccess = '';
            this.isGenerating = false;
        });
    }

    saveFormalizationActivity() {
        const endTime = new Date();
        const startTime = this.requestStartTime || endTime;
        const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000);
        const userDetails = this.authService.getUserDetails();

        const payloads = this.items
            .filter(item => item && (item as any).generated && (item as any).variants && (item as any).variants.length > 0)
            .map(item => {
                const chosen = (item as any).selectedVariantIndex !== -1 && (item as any).variants[(item as any).selectedVariantIndex]
                    ? (item as any).variants[(item as any).selectedVariantIndex].text
                    : (item as any).variants[0].text;
                return new FormalizePayload({
                    duration: duration,
                    dateRange: {
                        startDate: startTime.toISOString(),
                        endDate: endTime.toISOString()
                    },
                    sourceScreen: this.sourceScreen || null,
                    company: this.company ? new FormalizeCompany(this.company.companyId, this.company.companyName, '', '') : null,
                    jobMatch: this.jobMatch || null,
                    requisition: this.requisition || null,
                    candidate: this.candidate || null,
                    user: userDetails ? new FormalizeUser(
                        userDetails.id,
                        userDetails.firstname,
                        userDetails.lastname,
                        userDetails.emailId
                    ) : null,
                    originalText: item.text || '',
                    formalizedText: chosen,
                    tone: this.tone,
                    savedNote: null,
                    noteId: this.noteId || null
                });
            });

        if (payloads.length === 0) return;

        this.llm.saveBulkTextFormalization(payloads, (response) => {
            // Do Nothing
        }, (error) => {
            console.error('Failed to save bulk formalization activity', error);
        });
    }

    selectVariant(item: any, idx: number) {
        item.selectedVariantIndex = idx;
    }

    canEnhance(): boolean {
        if (this.isGenerating) return false;
        if (!this.items || !Array.isArray(this.items)) return false;
        return this.items.some(i => i && i.text && String(i.text).trim().length > 0);
    }

    save() {
        const out = this.items.map(item => {
            const chosen = (item as any).selectedVariantIndex !== -1 && (item as any).variants && (item as any).variants[(item as any).selectedVariantIndex]
                ? (item as any).variants[(item as any).selectedVariantIndex].text
                : item.text;
            return { key: item.key, label: item.label, text: chosen };
        });
        this.saveCallback.emit(out);
    }
}
